import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '../../services/translate/translate.service';
import { Address } from '../../interfaces/address.interface';
import { Subject, takeUntil, tap } from 'rxjs';

declare var google: any;

@Component({
  selector: 'app-address-modal',
  templateUrl: './address-modal.component.html',
  styleUrls: ['./address-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AddressModalComponent,
      multi: true,
    },
  ],
})
export class AddressModalComponent
  implements AfterViewInit, ControlValueAccessor, OnDestroy
{
  @ViewChild('address', { static: true }) addressInput!: ElementRef;
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  map!: any;
  marker!: any;
  geocoder = new google.maps.Geocoder();
  unsubscribe$ = new Subject();
  dataTranslateRegister: any;
  formAddress: Address = {
    address: '',
    references: '',
    lat: '',
    lng: '',
    country: '',
    state: '',
    city: '',
    zipCode: '',
  };

  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddressModalComponent>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngAfterViewInit() {
    this.dataTranslate();
    if (
      typeof google === 'undefined' ||
      typeof google.maps === 'undefined' ||
      typeof google.maps.places === 'undefined'
    ) {
      console.error('Google Maps API is not available.');
      return;
    }

    const input = this.addressInput.nativeElement;
    const autocomplete = new google.maps.places.Autocomplete(input);

    this.initMap();

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        this.formAddress.address = place.formatted_address;
        this.formAddress.lat = place.geometry.location.lat().toFixed(6);
        this.formAddress.lng = place.geometry.location.lng().toFixed(6);
        this.formAddress.country = this.getAddressComponent(place, 'country');
        this.formAddress.state = this.getAddressComponent(
          place,
          'administrative_area_level_1'
        );
        this.formAddress.city = this.getAddressComponent(place, 'locality');
        this.formAddress.zipCode = this.getAddressComponent(
          place,
          'postal_code'
        );
        this.updateFormControl();
        this.cdr.detectChanges();

        // Update map position
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(15);

        // Update marker position
        this.marker.setPosition(place.geometry.location);
        this.marker.setVisible(true);
      } else {
        console.log('No details available for input: ' + input.value);
      }
    });

    // Add event listener for marker drag end
    google.maps.event.addListener(this.marker, 'dragend', () => {
      const position = this.marker.getPosition();
      if (position) {
        this.formAddress.lat = position.lat().toFixed(6).toString();
        this.formAddress.lng = position.lng().toFixed(6).toString();
        this.geocodePosition(position);
      }
    });
  }

  dataTranslate() {
    this.translateService
      .init('core')
      .pipe(
        tap((data) => {
          this.dataTranslateRegister = data.login.register.modalAddress;
          this.cdr.detectChanges();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }
  initMap() {
    const defaultMapOptions = {
      center: { lat: -34.397, lng: 150.644 },
      zoom: 8,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: false,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      rotateControl: false,
      fullscreenControl: false,
      dataTapDisabled: true,
    };

    this.map = new google.maps.Map(
      this.mapContainer.nativeElement,
      defaultMapOptions
    );
    this.marker = new google.maps.Marker({
      map: this.map,
      visible: false,
      draggable: true,
    });
    if (this.data.city && this.data.city.length > 0) {
      this.formAddress = this.data;
      const position = {
        lat: parseFloat(this.formAddress.lat),
        lng: parseFloat(this.formAddress.lng),
      };

      // Establece la posición en el mapa
      this.map.setCenter(position);
      this.map.setZoom(15);

      // Establece la posición del marcador
      this.marker.setPosition(position);
      this.marker.setVisible(true);
      this.cdr.detectChanges();
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            this.map.setCenter(pos);
            this.map.setZoom(15);
            this.marker.setPosition(pos);
            this.marker.setVisible(true);
            this.formAddress.lat = pos.lat.toFixed(6).toString();
            this.formAddress.lng = pos.lng.toFixed(6).toString();
            this.geocodePosition(pos);
          },
          () => {
            console.log('Error: The Geolocation service failed.');
            this.map.setCenter(defaultMapOptions.center);
            this.marker.setPosition(defaultMapOptions.center);
            this.marker.setVisible(true);
          }
        );
      } else {
        console.log("Error: Your browser doesn't support geolocation.");
        this.map.setCenter(defaultMapOptions.center);
        this.marker.setPosition(defaultMapOptions.center);
        this.marker.setVisible(true);
      }
    }

    const locationButton = document.createElement('button');
    locationButton.textContent = '📍';
    locationButton.classList.add('custom-map-control-button');
    this.map.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(
      locationButton
    );

    // Obtener la geolocalización cuando se hace clic en el botón
    locationButton.addEventListener('click', () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            // Centrar el mapa en la ubicación del usuario
            this.map.setCenter(userLocation);
            this.map.setZoom(15);

            // Colocar el marcador en la ubicación del usuario
            this.marker.setPosition(userLocation);
            this.marker.setVisible(true);

            // Actualizar el formulario con las coordenadas
            this.formAddress.lat = userLocation.lat.toFixed(6);
            this.formAddress.lng = userLocation.lng.toFixed(6);
            this.geocodePosition(userLocation); // Obtener la dirección textual
          },
          () => {
            console.log('No se pudo obtener la ubicación.');
          }
        );
      } else {
        console.log('Geolocation no es soportado por este navegador.');
      }
    });
  }

  geocodePosition(position: any) {
    this.geocoder.geocode(
      { location: position },
      (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            this.formAddress.address = results[0].formatted_address;
            this.formAddress.country = this.getAddressComponent(
              results[0],
              'country'
            );
            this.formAddress.state = this.getAddressComponent(
              results[0],
              'administrative_area_level_1'
            );
            this.formAddress.city = this.getAddressComponent(
              results[0],
              'locality'
            );
            this.formAddress.zipCode = this.getAddressComponent(
              results[0],
              'postal_code'
            );
            this.updateFormControl();
            this.cdr.detectChanges();
          }
        }
      }
    );
  }

  getAddressComponent(place: any, type: string) {
    const component = place.address_components.find((comp: any) =>
      comp.types.includes(type)
    );
    return component ? component.long_name : '';
  }

  updateFormControl() {
    this.onChange(this.formAddress);
    this.onTouched();
    this.cdr.markForCheck();
  }

  onInputChange(
    event: any,
    field: 'address' | 'references' | 'country' | 'city' | 'zipCode' | 'state'
  ) {
    this.formAddress[field] = event.target.value as any;
    this.updateFormControl();
  }

  mapZoom(direction: 'in' | 'out') {
    if (direction === 'in') {
      this.map.setZoom(this.map.getZoom() + 1);
    }
    if (direction === 'out') {
      this.map.setZoom(this.map.getZoom() - 1);
    }
  }

  // ControlValueAccessor
  writeValue(value: any): void {
    if (value) {
      this.formAddress = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  closeModal() {
    this.dialogRef.close(this.formAddress);
  }
  locateUser() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // Centra el mapa en la posición del usuario
          this.map.setCenter(userPosition);
          this.map.setZoom(15);

          // Mueve el marcador a la posición del usuario
          this.marker.setPosition(userPosition);
          this.marker.setVisible(true);

          // Actualiza el formAddress con la latitud y longitud
          this.formAddress.lat = userPosition.lat.toFixed(6).toString();
          this.formAddress.lng = userPosition.lng.toFixed(6).toString();

          // Geocodifica la posición para obtener la dirección
          this.geocodePosition(userPosition);
        },
        (error) => {
          console.error('Error al obtener la ubicación:', error);
        }
      );
    } else {
      console.error('El navegador no soporta Geolocalización.');
    }
  }

  addForm() {
    this.dialogRef.close(this.formAddress);
  }

  ngOnDestroy(): void {
    this.unsubscribe$?.next(null);
    this.unsubscribe$?.complete();
  }
}
